import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Space, Typography, message } from "antd";
import {
  fetchThanksgivingSettings,
  findThanksgivingConfigByInterval,
  getThanksgivingApiError,
  saveThanksgivingConfig,
} from "../../../utils/thanksgivingConfigApi";
import {
  getSelectedInterval,
  loadThanksgivingConfigCache,
  saveThanksgivingConfigCache,
  setSelectedInterval,
} from "../../../utils/thanksgivingConfigStorage";
import { getStoredUser } from "../../../utils/authSession";
import "../common.css";
import "../../batch-monitor/JobConfiguration/index.css";
import "./index.css";

const { Text } = Typography;

const INTERVAL_OPTIONS = [
  { value: "2", label: "2 Hourly" },
  { value: "4", label: "4 Hourly" },
];

function normalizeIntervalHours(value) {
  const n = String(value ?? "").trim();
  return n === "4" ? "4" : "2";
}

const EMPTY_FORM = {
  fromDate: "",
  toDate: "",
  ricefIds: [],
  distributionIds: [],
  senderEmail: [],
};

function dedupeIds(ids) {
  const seen = new Set();
  const result = [];
  ids.forEach((id) => {
    const trimmed = String(id ?? "").trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(trimmed);
  });
  return result;
}

function extractFormIds(values) {
  const entries = values?.ids;
  if (!Array.isArray(entries)) return [];
  return dedupeIds(entries.map((value) => String(value ?? "").trim()).filter(Boolean));
}

function applyConfigToForm(config) {
  if (!config) return { ...EMPTY_FORM };
  return {
    fromDate: config.fromDate || "",
    toDate: config.toDate || "",
    ricefIds: Array.isArray(config.ricefIds) ? config.ricefIds : [],
    distributionIds: Array.isArray(config.distributionIds) ? config.distributionIds : [],
    senderEmail: Array.isArray(config.senderEmail) ? config.senderEmail : [],
  };
}

/* ─── Reusable ID list section (table + Add/Edit modal) ─── */
function IdListSection({
  title,
  singularLabel,
  emptyMessage,
  ids,
  loading,
  disabled,
  onSave,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const openAddModal = () => {
    setModalMode("add");
    setModalOpen(true);
  };

  const openEditModal = () => {
    setModalMode("edit");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const syncFormWhenModalOpens = useCallback(
    (open) => {
      if (!open) return;
      if (modalMode === "edit") {
        form.setFieldsValue({ ids: ids.length > 0 ? [...ids] : [""] });
        return;
      }
      form.setFieldsValue({ ids: [""] });
    },
    [form, ids, modalMode]
  );

  const persistIds = async (nextIds, successMessage) => {
    setSubmitting(true);
    try {
      const data = await onSave(nextIds);
      message.success(data?.message || successMessage);
      closeModal();
    } catch (err) {
      message.error(getThanksgivingApiError(err, `Failed to save ${singularLabel}`));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (values) => {
    const submittedIds = extractFormIds(values);
    if (submittedIds.length === 0) {
      message.warning(`Enter at least one ${singularLabel}`);
      return;
    }

    if (modalMode === "add") {
      const nextIds = dedupeIds([...ids, ...submittedIds]);
      if (nextIds.length === ids.length) {
        message.warning(`These ${singularLabel}s are already configured`);
        return;
      }
      await persistIds(nextIds, `${singularLabel} added`);
      return;
    }

    await persistIds(submittedIds, `${singularLabel} updated`);
  };

  const modalTitle = modalMode === "edit" ? `Edit ${title}` : `Add ${title}`;
  const modalSubtitle =
    modalMode === "edit"
      ? `Update or remove ${singularLabel}s below. Use + to add more rows.`
      : `Enter one or more ${singularLabel}s. Use + to add more rows.`;

  return (
    <section className="tg-config-section tg-config-section-sub">
      <div className="tg-config-section-header">
        <h3 className="tg-config-section-subtitle">{title}</h3>
        <div className="admin-toolbar tg-config-section-toolbar">
          <button
            type="button"
            className="admin-btn primary"
            onClick={openAddModal}
            disabled={loading || disabled || submitting}
          >
            Add {title}
          </button>
          {ids.length > 0 && (
            <button
              type="button"
              className="admin-btn"
              onClick={openEditModal}
              disabled={loading || disabled || submitting}
            >
              Edit {title}
            </button>
          )}
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table job-config-table">
          <thead>
            <tr>
              <th>{title}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="admin-empty">Loading {title.toLowerCase()}…</td>
              </tr>
            ) : ids.length === 0 ? (
              <tr>
                <td className="admin-empty">{emptyMessage}</td>
              </tr>
            ) : (
              ids.map((id) => (
                <tr key={id}>
                  <td className="admin-td-name">{id || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        title={
          <div className="job-config-modal-title">
            <span className="job-config-modal-heading">{modalTitle}</span>
            <Text type="secondary" className="job-config-modal-subtitle">
              {modalSubtitle}
            </Text>
          </div>
        }
        open={modalOpen}
        onCancel={closeModal}
        afterOpenChange={syncFormWhenModalOpens}
        destroyOnClose
        width={620}
        className="job-config-modal"
        footer={null}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          size="middle"
          className="job-config-form tg-id-list-form"
          onFinish={onSubmit}
          initialValues={{ ids: [""] }}
        >
          <Form.List name="ids">
            {(fields, { add, remove }) => (
              <div className="tg-id-list-fields">
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} className="tg-id-list-row">
                    <Form.Item
                      {...restField}
                      name={name}
                      label={fields.length > 1 ? `${singularLabel} ${name + 1}` : singularLabel}
                      rules={[
                        { required: true, message: `Enter a ${singularLabel}` },
                        { whitespace: true, message: `${singularLabel} cannot be only spaces` },
                      ]}
                      className="tg-id-list-item"
                    >
                      <Input
                        allowClear
                        placeholder={`Enter ${singularLabel}`}
                        autoComplete="off"
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        className="admin-btn link danger tg-id-list-remove"
                        onClick={() => remove(name)}
                        aria-label={`Remove ${singularLabel}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <Button type="dashed" onClick={() => add("")} block className="tg-id-list-add">
                  + Add {singularLabel}
                </Button>
              </div>
            )}
          </Form.List>
          <div className="job-config-form-footer">
            <Space>
              <Button onClick={closeModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {modalMode === "edit" ? "Save changes" : `Add ${title}`}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </section>
  );
}

/* ─── Main page ─── */
const ThanksgivingConfiguration = () => {
  const [hours, setHours] = useState("2");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [ricefIds, setRicefIds] = useState([]);
  const [distributionIds, setDistributionIds] = useState([]);
  const [senderEmail, setSenderEmail] = useState([]);
  const [allConfigs, setAllConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingInterval, setSwitchingInterval] = useState(false);
  const [savingInterval, setSavingInterval] = useState(false);

  const currentUserEmail = useMemo(() => {
    const user = getStoredUser();
    return (user?.email || user?.username || "").trim();
  }, []);

  const applyIntervalConfig = useCallback(
    (interval, configs) => {
      const normalized = normalizeIntervalHours(interval);
      const matched = findThanksgivingConfigByInterval(configs, normalized);
      const form = applyConfigToForm(matched);

      setHours(normalized);
      setFromDate(form.fromDate);
      setToDate(form.toDate);
      setRicefIds(form.ricefIds);
      setDistributionIds(form.distributionIds);
      setSenderEmail(
        form.senderEmail.length > 0 ? form.senderEmail : [currentUserEmail].filter(Boolean)
      );
      setSelectedInterval(normalized);
    },
    [currentUserEmail]
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const remote = await fetchThanksgivingSettings();
      const cached = loadThanksgivingConfigCache();
      const configs = remote.length > 0 ? remote : cached;

      setAllConfigs(configs);
      saveThanksgivingConfigCache(configs);

      const preferredInterval = normalizeIntervalHours(
        getSelectedInterval() || configs[0]?.interval || "2"
      );

      applyIntervalConfig(preferredInterval, configs);
    } catch (err) {
      const cached = loadThanksgivingConfigCache();
      setAllConfigs(cached);
      applyIntervalConfig(
        normalizeIntervalHours(getSelectedInterval() || cached[0]?.interval || "2"),
        cached
      );
      message.error(getThanksgivingApiError(err, "Failed to load Thanksgiving configuration"));
    } finally {
      setLoading(false);
    }
  }, [applyIntervalConfig]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleHoursChange = async (nextHours) => {
    const normalized = normalizeIntervalHours(nextHours);
    setSwitchingInterval(true);
    try {
      const remote = await fetchThanksgivingSettings(normalized);
      const configs = remote.length > 0 ? remote : loadThanksgivingConfigCache();
      setAllConfigs(configs);
      saveThanksgivingConfigCache(configs);
      applyIntervalConfig(normalized, configs);
    } catch (err) {
      applyIntervalConfig(normalized, allConfigs);
      message.error(
        getThanksgivingApiError(err, "Failed to load configuration for selected interval")
      );
    } finally {
      setSwitchingInterval(false);
    }
  };

  const saveWholeConfig = useCallback(
    async (overrides = {}) => {
      const next = {
        interval: overrides.hours ?? hours,
        fromDate: overrides.fromDate ?? fromDate,
        toDate: overrides.toDate ?? toDate,
        ricefIds: overrides.ricefIds ?? ricefIds,
        distributionIds: overrides.distributionIds ?? distributionIds,
        senderEmail: overrides.senderEmail ?? senderEmail,
      };

      const data = await saveThanksgivingConfig(next);

      const updatedConfigs = [...allConfigs];
      const index = updatedConfigs.findIndex(
        (item) => String(item.interval).trim() === String(next.interval).trim()
      );
      const normalized = {
        interval: String(next.interval),
        fromDate: next.fromDate,
        toDate: next.toDate,
        ricefIds: next.ricefIds,
        distributionIds: next.distributionIds,
        senderEmail: next.senderEmail,
      };

      if (index >= 0) {
        updatedConfigs[index] = { ...updatedConfigs[index], ...normalized };
      } else {
        updatedConfigs.push(normalized);
      }

      setAllConfigs(updatedConfigs);
      saveThanksgivingConfigCache(updatedConfigs);

      return data;
    },
    [hours, fromDate, toDate, ricefIds, distributionIds, senderEmail, allConfigs]
  );

  const saveInterval = async () => {
    if (!String(hours).trim()) {
      message.warning("Please select an interval time");
      return;
    }
    if (fromDate && toDate && fromDate > toDate) {
      message.error("Data From date cannot be later than Data To date");
      return;
    }
    setSavingInterval(true);
    try {
      await saveWholeConfig({ hours, fromDate, toDate });
      message.success("Interval configuration saved");
    } catch (err) {
      message.error(getThanksgivingApiError(err, "Failed to save interval configuration"));
    } finally {
      setSavingInterval(false);
    }
  };

  const saveRicefIds = async (nextIds) => {
    const data = await saveWholeConfig({ ricefIds: nextIds });
    setRicefIds(nextIds);
    return data;
  };

  const saveDistributionIds = async (nextIds) => {
    const data = await saveWholeConfig({ distributionIds: nextIds });
    setDistributionIds(nextIds);
    return data;
  };

  const saveSenderEmail = async (nextIds) => {
    const data = await saveWholeConfig({ senderEmail: nextIds });
    setSenderEmail(nextIds);
    return data;
  };

  return (
    <div className="admin-page-container job-config-page">
      <div className="admin-page-content">
        <div className="header-section" style={{ marginTop: "2rem" }}>
          <h1 className="page-title">Thanksgiving Configuration</h1>
          <p className="page-subtitle">
            Configure RICEF IDs, Distribution IDs, and interval time for Thanksgiving
            Monitoring. Select an interval to load its saved configuration.
          </p>
        </div>

        {/* ── Interval time ── */}
        <div className="job-config-section-interval tg-interval-radio-wrap" style={{ marginTop: "1.5rem" }}>
          <span className="job-config-section-interval-label">Interval time</span>
          <div className="tg-radio-group">
            {INTERVAL_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`tg-radio-option${hours === opt.value ? " tg-radio-option-active" : ""}`}
              >
                <input
                  type="radio"
                  name="tg-interval"
                  value={opt.value}
                  checked={hours === opt.value}
                  onChange={() => handleHoursChange(opt.value)}
                  disabled={loading || savingInterval || switchingInterval}
                  className="tg-radio-input"
                />
                <span className="tg-radio-label">{opt.label}</span>
              </label>
            ))}
          </div>
          <Button
            type="primary"
            loading={savingInterval}
            onClick={saveInterval}
            disabled={loading}
          >
            Save
          </Button>
        </div>

        {/* ── Data Date Range ── */}
        <div className="job-config-section-interval">
          <span className="job-config-section-interval-label">Data From</span>
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
            disabled={loading}
            style={{
              padding: "5px 10px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
              height: "32px",
              minWidth: "160px",
            }}
          />
          <span className="job-config-section-interval-label" style={{ minWidth: "auto" }}>To</span>
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
            disabled={loading}
            style={{
              padding: "5px 10px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
              height: "32px",
              minWidth: "160px",
            }}
          />
        </div>

        {/* ── RICEF IDs ── */}
        <IdListSection
          title="RICEF ID"
          singularLabel="RICEF ID"
          emptyMessage="No RICEF IDs configured. Add a RICEF ID to get started."
          ids={ricefIds}
          loading={loading}
          disabled={savingInterval || switchingInterval}
          onSave={saveRicefIds}
        />

        {/* ── Distribution IDs ── */}
        <IdListSection
          title="Distribution ID"
          singularLabel="Distribution ID"
          emptyMessage="No Distribution IDs configured. Add a Distribution ID to get started."
          ids={distributionIds}
          loading={loading}
          disabled={savingInterval || switchingInterval}
          onSave={saveDistributionIds}
        />

        {/* ── Sender Email ── */}
        <IdListSection
          title="Sender Email"
          singularLabel="Sender Email"
          emptyMessage="No sender email configured. Add a sender email to get started."
          ids={senderEmail}
          loading={loading}
          disabled={savingInterval || switchingInterval}
          onSave={saveSenderEmail}
        />
      </div>
    </div>
  );
};

export default ThanksgivingConfiguration;
